import { useState, useCallback, useMemo } from 'react';
import { isEmpty } from '../utils/helpers';

/**
 * Custom hook for filtering data
 * @param {array} items - Array of items to filter
 * @param {object} initialFilters - Initial filter values
 * @returns {object} Filtering state and handlers
 */
const useFiltering = (items = [], initialFilters = {}) => {
  const [filters, setFilters] = useState(initialFilters);
  
  // Set a single filter value
  const setFilter = useCallback((key, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [key]: value
    }));
  }, []);
  
  // Set multiple filter values at once
  const setMultipleFilters = useCallback((newFilters) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
  }, []);
  
  // Remove a filter
  const removeFilter = useCallback((key) => {
    setFilters(prevFilters => {
      const newFilters = { ...prevFilters };
      delete newFilters[key];
      return newFilters;
    });
  }, []);
  
  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);
  
  // Reset filters to initial values
  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
  }, [initialFilters]);
  
  // Check if a value matches a filter
  const matchesFilter = useCallback((item, filterKey, filterValue) => {
    // If filter value is empty, consider it a match
    if (isEmpty(filterValue)) return true;
    
    // Handle nested properties (e.g., 'user.name')
    const getNestedValue = (obj, path) => {
      return path.split('.').reduce((o, key) => (o && o[key] !== undefined) ? o[key] : null, obj);
    };
    
    const itemValue = getNestedValue(item, filterKey);
    
    // If item doesn't have the property, no match
    if (itemValue === null || itemValue === undefined) return false;
    
    // Handle different filter types
    if (typeof filterValue === 'function') {
      // Custom filter function
      return filterValue(itemValue);
    } else if (Array.isArray(filterValue)) {
      // Array of possible values (OR condition)
      return filterValue.some(val => 
        String(itemValue).toLowerCase() === String(val).toLowerCase()
      );
    } else if (typeof filterValue === 'object' && filterValue !== null) {
      // Range filter with min/max
      const { min, max } = filterValue;
      if (min !== undefined && max !== undefined) {
        return itemValue >= min && itemValue <= max;
      } else if (min !== undefined) {
        return itemValue >= min;
      } else if (max !== undefined) {
        return itemValue <= max;
      }
      return true;
    } else if (typeof itemValue === 'string' && typeof filterValue === 'string') {
      // String contains (case-insensitive)
      return itemValue.toLowerCase().includes(filterValue.toLowerCase());
    } else if (typeof itemValue === 'number' && typeof filterValue === 'number') {
      // Exact number match
      return itemValue === filterValue;
    } else if (typeof itemValue === 'boolean' && typeof filterValue === 'boolean') {
      // Exact boolean match
      return itemValue === filterValue;
    } else {
      // Default: convert both to strings and compare (case-insensitive)
      return String(itemValue).toLowerCase() === String(filterValue).toLowerCase();
    }
  }, []);
  
  // Get filtered items
  const filteredItems = useMemo(() => {
    // If no filters are applied, return all items
    if (Object.keys(filters).length === 0) return items;
    
    return items.filter(item => {
      // Item must match all filters (AND condition)
      return Object.entries(filters).every(([key, value]) => 
        matchesFilter(item, key, value)
      );
    });
  }, [items, filters, matchesFilter]);
  
  // Check if any filters are applied
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(value => !isEmpty(value));
  }, [filters]);
  
  // Get active filter count
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(value => !isEmpty(value)).length;
  }, [filters]);
  
  return {
    filteredItems,
    filters,
    hasActiveFilters,
    activeFilterCount,
    setFilter,
    setMultipleFilters,
    removeFilter,
    clearFilters,
    resetFilters,
  };
};

export default useFiltering;
